import { useApiClient } from '@/api/ApiClientProvider'
import type { Ga4Property } from '@/api/types'
import { Card, EmptyState, useNow } from '@/design'

import { useIntegrationWebsites, websiteLabelFor } from '../useIntegrationWebsites'
import { Ga4Error } from './errors'
import { PropertyRow } from './PropertyRow'
import { useGa4Mutation } from './useGa4Mutation'

const CONFIRM_DISCONNECT =
  'Disconnect this GA4 property? Its traffic stops updating until someone connects it again. Data already imported is kept.'

/** Connected properties and their health: the table shell, the queries and the two mutations. */
export function PropertiesCard({ properties, email }: { properties: readonly Ga4Property[]; email: string }) {
  const client = useApiClient()
  const now = useNow(30_000)
  const websites = useIntegrationWebsites()

  const runNow = useGa4Mutation({ mutationFn: (id: string) => client.runGa4Import(id) })
  const disconnect = useGa4Mutation({ mutationFn: (id: string) => client.disconnectGa4(id) })
  const busy = runNow.isPending || disconnect.isPending
  /** One error at a time: starting either action clears what the last one left on screen. */
  const act = (start: () => void) => {
    runNow.reset()
    disconnect.reset()
    start()
  }

  return (
    <Card title="Connected properties" subtitle="Traffic is imported daily; Run import now re-imports the last three days.">
      {properties.length === 0 ? (
        <EmptyState variant="disconnected" compact title="No property connected" description="Connect a property above to start importing traffic." />
      ) : (
        <div className="ci-table-wrap">
          <table className="ci-table">
            <thead>
              <tr>
                <th scope="col">Property</th>
                <th scope="col">Website</th>
                <th scope="col">Status</th>
                <th scope="col">Last import</th>
                <th scope="col">Freshness</th>
                <th scope="col">
                  <span className="ci-visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {properties.map((property) => (
                <PropertyRow
                  key={property.ga4PropertyId}
                  property={property}
                  websiteName={property.websiteId ? websiteLabelFor(websites.data ?? [], property.websiteId) : '—'}
                  now={now}
                  busy={busy}
                  onRunNow={() => act(() => runNow.mutate(property.ga4PropertyId))}
                  onDisconnect={() => window.confirm(CONFIRM_DISCONNECT) && act(() => disconnect.mutate(property.ga4PropertyId))}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Ga4Error error={runNow.error ?? disconnect.error} email={email} fallback="That did not work. Try again in a moment." />
    </Card>
  )
}
